import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

function auth() { return { headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } }; }
function fmtBytes(b) { if (!b) return '—'; if (b<1024) return `${b}B`; if (b<1048576) return `${(b/1024).toFixed(1)}KB`; return `${(b/1048576).toFixed(1)}MB`; }
function ts(d) { if (!d) return '—'; const m=Math.floor((Date.now()-new Date(d))/60000); return m<60?`${m}m ago`:m<1440?`${Math.floor(m/60)}h ago`:`${Math.floor(m/1440)}d ago`; }

const CAT_META = {
  script:      { icon:'📜', color:'#1d4ed8', bg:'#dbeafe' },
  installer:   { icon:'📦', color:'#7c3aed', bg:'#ede9fe' },
  config:      { icon:'⚙️',  color:'#d97706', bg:'#fef3c7' },
  certificate: { icon:'🔐', color:'#dc2626', bg:'#fee2e2' },
  other:       { icon:'📄', color:'#64748b', bg:'#f1f5f9' },
};
const OS_META = {
  windows: { icon:'🪟', label:'Windows' },
  linux:   { icon:'🐧', label:'Linux'   },
  macos:   { icon:'🍎', label:'macOS'   },
  all:     { icon:'🌐', label:'All OS'  },
};

export default function FileStore({ user }) {
  const [files,    setFiles]    = useState([]);
  const [devices,  setDevices]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [msg,      setMsg]      = useState({ text:'', type:'' });
  const [showForm, setShowForm] = useState(false);
  const [deployModal, setDeployModal] = useState(null);
  const [selDevs,  setSelDevs]  = useState([]);
  const [deploying,setDeploying]= useState(false);
  const [catFilter,setCatFilter]= useState('all');
  const [search,   setSearch]   = useState('');
  const fileInput = useRef();
  const [form, setForm] = useState({ name:'', description:'', category:'script', osTarget:'all', deployPath:'', runAfterDeploy:false, runCommand:'' });
  const [uploading, setUploading] = useState(false);

  function flash(t,type='success') { setMsg({text:t,type}); setTimeout(()=>setMsg({text:'',type:''}),4000); }

  const load = useCallback(async () => {
    try {
      const [fRes, dRes] = await Promise.all([
        axios.get('/api/file-store', auth()),
        axios.get('/api/agents', auth()),
      ]);
      setFiles(fRes.data);
      setDevices(dRes.data.filter?dRes.data.filter(d=>!d.archivedAt):dRes.data);
    } catch(e) { flash(e.response?.data?.error||'Failed to load','error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(()=>{ load(); },[load]);

  async function upload(e) {
    e.preventDefault();
    const file = fileInput.current?.files?.[0];
    if (!file) return flash('Select a file to upload','error');
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const base64 = ev.target.result.split(',')[1];
        await axios.post('/api/file-store', {
          ...form, filename: file.name,
          content: base64, mimeType: file.type || 'application/octet-stream',
        }, auth());
        flash('File uploaded successfully');
        setShowForm(false);
        setForm({ name:'', description:'', category:'script', osTarget:'all', deployPath:'', runAfterDeploy:false, runCommand:'' });
        if (fileInput.current) fileInput.current.value = '';
        load();
      };
      reader.readAsDataURL(file);
    } catch(e) {
      flash(e.response?.data?.error||'Upload failed','error');
      setUploading(false);
    }
  }

  async function del(id) {
    if (!window.confirm('Delete this file?')) return;
    try { await axios.delete(`/api/file-store/${id}`, auth()); flash('Deleted'); load(); }
    catch(e) { flash('Failed to delete','error'); }
  }

  async function deploy() {
    if (!selDevs.length) return flash('Select at least one device','error');
    setDeploying(true);
    try {
      const r = await axios.post(`/api/file-store/${deployModal.id}/deploy`, { deviceIds: selDevs }, auth());
      flash(`Deployed to ${r.data.deployed} device(s)`);
      setDeployModal(null); setSelDevs([]);
    } catch(e) { flash(e.response?.data?.error||'Deploy failed','error'); }
    finally { setDeploying(false); }
  }

  const filtered = files.filter(f => {
    if (catFilter!=='all' && f.category!==catFilter) return false;
    if (search && !f.name.toLowerCase().includes(search.toLowerCase()) && !f.filename.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div style={{padding:'24px'}}>
      {msg.text && <div style={{padding:'10px 16px',borderRadius:8,marginBottom:16,background:msg.type==='error'?'#fee2e2':'#dcfce7',color:msg.type==='error'?'#dc2626':'#16a34a',fontWeight:600}}>{msg.type==='error'?'✘':'✔'} {msg.text}</div>}

      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div>
          <h2 style={{margin:0,fontSize:20,fontWeight:700,color:'#1e293b'}}>File Store</h2>
          <p style={{margin:'4px 0 0',color:'#64748b',fontSize:14}}>Upload scripts, installers and config files — push to endpoints on demand</p>
        </div>
        <button onClick={()=>setShowForm(true)} style={{background:'#1e40af',color:'#fff',border:'none',borderRadius:8,padding:'10px 20px',fontWeight:600,cursor:'pointer',fontSize:14}}>+ Upload File</button>
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:10,marginBottom:18,flexWrap:'wrap'}}>
        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search files..." style={{padding:'8px 14px',border:'1px solid #e2e8f0',borderRadius:8,fontSize:13,flex:1,minWidth:180}} />
        {['all','script','installer','config','certificate','other'].map(c=>(
          <button key={c} onClick={()=>setCatFilter(c)} style={{padding:'7px 14px',borderRadius:8,border:'1px solid'+(catFilter===c?'transparent':'#e2e8f0'),background:catFilter===c?'#1e40af':'#fff',color:catFilter===c?'#fff':'#475569',fontWeight:600,cursor:'pointer',fontSize:12,textTransform:'capitalize'}}>
            {c==='all'?'All':c}
          </button>
        ))}
      </div>

      {loading ? <div style={{textAlign:'center',padding:40,color:'#94a3b8'}}>Loading...</div> : filtered.length===0 ? (
        <div style={{textAlign:'center',padding:60,color:'#94a3b8'}}><div style={{fontSize:40,marginBottom:12}}>📁</div><div style={{fontWeight:600}}>No files {search||catFilter!=='all'?'match filters':'uploaded yet'}</div></div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:14}}>
          {filtered.map(f=>{
            const cm = CAT_META[f.category]||CAT_META.other;
            const om = OS_META[f.osTarget]||OS_META.all;
            return (
              <div key={f.id} style={{background:'#fff',border:'1px solid #e2e8f0',borderRadius:12,padding:18}}>
                <div style={{display:'flex',gap:12,marginBottom:10}}>
                  <div style={{width:44,height:44,borderRadius:10,background:cm.bg,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22,flexShrink:0}}>{cm.icon}</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:14,color:'#1e293b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.name}</div>
                    <div style={{fontSize:11,color:'#64748b',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.filename}</div>
                  </div>
                </div>
                {f.description && <div style={{fontSize:12,color:'#64748b',marginBottom:10}}>{f.description}</div>}
                <div style={{display:'flex',gap:8,marginBottom:12,flexWrap:'wrap'}}>
                  <span style={{background:cm.bg,color:cm.color,borderRadius:5,padding:'2px 7px',fontSize:11,fontWeight:700,textTransform:'capitalize'}}>{f.category}</span>
                  <span style={{background:'#f1f5f9',color:'#475569',borderRadius:5,padding:'2px 7px',fontSize:11,fontWeight:600}}>{om.icon} {om.label}</span>
                  <span style={{background:'#f1f5f9',color:'#475569',borderRadius:5,padding:'2px 7px',fontSize:11}}>{fmtBytes(f.sizeBytes)}</span>
                  <span style={{background:'#f1f5f9',color:'#94a3b8',borderRadius:5,padding:'2px 7px',fontSize:11}}>↓ {f.downloadCount||0}</span>
                </div>
                <div style={{fontSize:11,color:'#94a3b8',marginBottom:12}}>Uploaded {ts(f.createdAt)} by {f.uploadedBy||'admin'}</div>
                <div style={{display:'flex',gap:8}}>
                  <button onClick={()=>{setDeployModal(f);setSelDevs([]);}} style={{flex:1,background:'#1e40af',color:'#fff',border:'none',borderRadius:7,padding:'8px',fontSize:12,fontWeight:600,cursor:'pointer'}}>
                    🚀 Deploy
                  </button>
                  <a href={`/api/file-store/${f.id}/download`} target="_blank" rel="noreferrer" style={{background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:7,padding:'8px 12px',fontSize:12,fontWeight:600,cursor:'pointer',color:'#475569',textDecoration:'none'}}>
                    ↓
                  </a>
                  <button onClick={()=>del(f.id)} style={{background:'#fee2e2',border:'none',borderRadius:7,padding:'8px 10px',fontSize:12,cursor:'pointer',color:'#dc2626'}}>🗑</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload modal */}
      {showForm && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:14,padding:32,width:520,maxWidth:'95vw',maxHeight:'85vh',overflow:'auto'}}>
            <h3 style={{margin:'0 0 20px',fontSize:18,fontWeight:700}}>Upload File</h3>
            <form onSubmit={upload}>
              <div style={{marginBottom:14}}>
                <label style={{display:'block',fontWeight:600,fontSize:13,marginBottom:4,color:'#374151'}}>File*</label>
                <input type="file" ref={fileInput} required style={{width:'100%',padding:'8px',border:'1px solid #d1d5db',borderRadius:8,fontSize:13}} />
              </div>
              {[['Display Name*','name','e.g. Deploy Firefox'],['Description','description','Optional description']].map(([l,k,ph])=>(
                <div key={k} style={{marginBottom:14}}>
                  <label style={{display:'block',fontWeight:600,fontSize:13,marginBottom:4,color:'#374151'}}>{l}</label>
                  <input value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} placeholder={ph} required={l.includes('*')} style={{width:'100%',padding:'9px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,boxSizing:'border-box'}} />
                </div>
              ))}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
                <div><label style={{display:'block',fontWeight:600,fontSize:13,marginBottom:4,color:'#374151'}}>Category</label>
                  <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})} style={{width:'100%',padding:'9px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14}}>
                    {['script','installer','config','certificate','other'].map(c=><option key={c} value={c}>{c}</option>)}
                  </select></div>
                <div><label style={{display:'block',fontWeight:600,fontSize:13,marginBottom:4,color:'#374151'}}>OS Target</label>
                  <select value={form.osTarget} onChange={e=>setForm({...form,osTarget:e.target.value})} style={{width:'100%',padding:'9px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14}}>
                    <option value="all">All Platforms</option>
                    <option value="windows">Windows</option>
                    <option value="linux">Linux</option>
                    <option value="macos">macOS</option>
                  </select></div>
              </div>
              <div style={{marginBottom:14}}>
                <label style={{display:'block',fontWeight:600,fontSize:13,marginBottom:4,color:'#374151'}}>Deploy Path (optional)</label>
                <input value={form.deployPath} onChange={e=>setForm({...form,deployPath:e.target.value})} placeholder="e.g. C:\Scripts\deploy.ps1" style={{width:'100%',padding:'9px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,boxSizing:'border-box'}} />
              </div>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14}}>
                <input type="checkbox" id="runAfter" checked={form.runAfterDeploy} onChange={e=>setForm({...form,runAfterDeploy:e.target.checked})} />
                <label htmlFor="runAfter" style={{fontSize:13,fontWeight:600,color:'#374151'}}>Execute after deploying</label>
              </div>
              {form.runAfterDeploy && (
                <div style={{marginBottom:14}}>
                  <label style={{display:'block',fontWeight:600,fontSize:13,marginBottom:4,color:'#374151'}}>Run Command (optional)</label>
                  <input value={form.runCommand} onChange={e=>setForm({...form,runCommand:e.target.value})} placeholder="Leave blank to run the file directly" style={{width:'100%',padding:'9px 12px',border:'1px solid #d1d5db',borderRadius:8,fontSize:14,fontFamily:'monospace',boxSizing:'border-box'}} />
                </div>
              )}
              <div style={{display:'flex',gap:10,justifyContent:'flex-end',marginTop:8}}>
                <button type="button" onClick={()=>setShowForm(false)} style={{padding:'10px 20px',border:'1px solid #d1d5db',borderRadius:8,background:'#fff',cursor:'pointer',fontWeight:600}}>Cancel</button>
                <button type="submit" disabled={uploading} style={{padding:'10px 20px',background:uploading?'#94a3b8':'#1e40af',color:'#fff',border:'none',borderRadius:8,fontWeight:600,cursor:uploading?'default':'pointer'}}>
                  {uploading?'Uploading...':'Upload File'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Deploy modal */}
      {deployModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'#fff',borderRadius:14,padding:32,width:480,maxWidth:'95vw'}}>
            <h3 style={{margin:'0 0 4px',fontSize:18,fontWeight:700}}>Deploy: {deployModal.name}</h3>
            <p style={{margin:'0 0 16px',color:'#64748b',fontSize:13}}>{fmtBytes(deployModal.sizeBytes)} · {deployModal.category}</p>
            <div style={{fontWeight:600,fontSize:13,marginBottom:8,color:'#374151'}}>Select Devices</div>
            <div style={{display:'flex',gap:8,marginBottom:8}}>
              <button onClick={()=>setSelDevs(devices.filter(d=>d.status==='online').map(d=>d.id))} style={{fontSize:11,padding:'4px 10px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:5,cursor:'pointer'}}>All Online</button>
              <button onClick={()=>setSelDevs([])} style={{fontSize:11,padding:'4px 10px',background:'#f1f5f9',border:'1px solid #e2e8f0',borderRadius:5,cursor:'pointer'}}>Clear</button>
            </div>
            <div style={{maxHeight:200,overflow:'auto',border:'1px solid #e2e8f0',borderRadius:8,marginBottom:16}}>
              {devices.map(d=>(
                <label key={d.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',borderBottom:'1px solid #f8fafc',cursor:'pointer',background:selDevs.includes(d.id)?'#eff6ff':'#fff'}}>
                  <input type="checkbox" checked={selDevs.includes(d.id)} onChange={e=>setSelDevs(s=>e.target.checked?[...s,d.id]:s.filter(x=>x!==d.id))} />
                  <span style={{flex:1,fontSize:13,fontWeight:500}}>{d.hostname}</span>
                  <span style={{fontSize:11,color:d.status==='online'?'#16a34a':'#94a3b8',fontWeight:600}}>{d.status==='online'?'● Online':'○ Offline'}</span>
                </label>
              ))}
            </div>
            <div style={{display:'flex',gap:10,justifyContent:'flex-end'}}>
              <button onClick={()=>setDeployModal(null)} style={{padding:'10px 20px',border:'1px solid #d1d5db',borderRadius:8,background:'#fff',cursor:'pointer',fontWeight:600}}>Cancel</button>
              <button onClick={deploy} disabled={deploying||!selDevs.length} style={{padding:'10px 20px',background:deploying||!selDevs.length?'#94a3b8':'#1e40af',color:'#fff',border:'none',borderRadius:8,fontWeight:600,cursor:deploying?'default':'pointer'}}>
                {deploying ? 'Deploying...' : `Deploy to ${selDevs.length} device(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
